import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Helper to normalize and join URL paths
const joinUrl = (base, path) => {
  if (!base) return path;
  // Ensure base ends with /api if it's pointing to the API gateway
  let normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

// Robust fetch with retry and better error reporting
const formatDateSafe = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    let cleanStr = dateStr;
    if (dateStr.endsWith('Z') && dateStr.includes('+00:00')) {
      cleanStr = dateStr.replace('Z', '');
    }
    const date = new Date(cleanStr);
    let finalDate;
    if (isNaN(date.getTime())) {
      const altDate = new Date(dateStr.replace('Z', ''));
      if (isNaN(altDate.getTime())) return 'N/A';
      finalDate = altDate;
    } else {
      finalDate = date;
    }
    return finalDate.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch {
    return 'N/A';
  }
};

const robustFetch = async (url, options = {}, retries = 2) => {
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      ...options,
      headers: {
        ...options.headers,
      }
    });
    
    if (!response.ok) {
      // Try to parse error detail from JSON
      let errorDetail = response.statusText;
      try {
        const data = await response.json();
        errorDetail = data.detail || data.message || JSON.stringify(data);
      } catch (e) {
        // Fallback to status text
      }
      throw new Error(`HTTP ${response.status}: ${errorDetail}`);
    }
    return response;
  } catch (err) {
    if (retries > 0 && err.name !== 'AbortError') {
      console.warn(`Fetch failed, retrying... (${retries} left). Error: ${err.message}`);
      await new Promise(res => setTimeout(res, 1000)); // Wait 1s before retry
      return robustFetch(url, options, retries - 1);
    }
    throw err;
  }
};

function UploadJobs() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('tag');
  const [jobs, setJobs] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [error, setError] = useState(null);
  const pollingRef = useRef({});

  // Use state for config to handle dynamic configuration
  const [config] = useState(() => {
    const savedConfig = localStorage.getItem('scada_api_config');
    const parsed = savedConfig ? JSON.parse(savedConfig) : {};
    return {
      apiBase: parsed.apiUrl || process.env.REACT_APP_API_BASE || 'http://localhost:20021/api',
      apiKey: parsed.apikey !== undefined ? parsed.apikey : (process.env.REACT_APP_API_KEY || '')
    };
  });

  const getHeaders = useCallback(() => {
    const headers = {};
    if (config.apiKey) headers['apikey'] = config.apiKey;
    return headers;
  }, [config.apiKey]);

  const fetchJobStatus = useCallback(async (jobId) => {
    try {
      const response = await robustFetch(joinUrl(config.apiBase, `status/${jobId}`), {
        headers: getHeaders()
      });
      const data = await response.json();
      setJobs(prev => ({ ...prev, [jobId]: data }));
      return data.status;
    } catch (err) {
      console.error(`Status fetch error for ${jobId}:`, err);
      return null;
    }
  }, [config.apiBase, getHeaders]);

  const pollJobStatus = useCallback((jobId) => {
    if (pollingRef.current[jobId]) {
      clearInterval(pollingRef.current[jobId]);
    }
    
    pollingRef.current[jobId] = setInterval(async () => {
      const status = await fetchJobStatus(jobId);
      if (status === 'completed' || status === 'failed') {
        clearInterval(pollingRef.current[jobId]);
        delete pollingRef.current[jobId];
      }
    }, 2000);
  }, [fetchJobStatus]);

  const fetchAllJobs = useCallback(async () => {
    try {
      const response = await robustFetch(joinUrl(config.apiBase, 'all_jobs/'), {
        headers: getHeaders()
      });
      const data = await response.json();
      const jobsMap = {};
      data.forEach(job => {
        jobsMap[job.job_id] = job;
      });
      setJobs(jobsMap);
      
      // Start polling for any non-completed jobs
      data.forEach(job => {
        if (job.status !== 'completed' && job.status !== 'failed') {
          pollJobStatus(job.job_id);
        }
      });
    } catch (err) {
      console.error('Fetch all jobs error:', err);
      setError(`Failed to sync with server: ${err.message}`);
    }
  }, [config.apiBase, getHeaders, pollJobStatus]);

  useEffect(() => {
    fetchAllJobs();
    
    return () => {
      // Clean up all intervals on unmount
      Object.values(pollingRef.current).forEach(interval => clearInterval(interval));
    };
  }, [fetchAllJobs]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file');
      return;
    }

    setLoading(true);
    setError(null);
    setUploadStatus(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await robustFetch(joinUrl(config.apiBase, `upload?file_type=${fileType}`), {
        method: 'POST',
        headers: getHeaders(),
        body: formData,
        mode: 'cors'
      });

      const data = await response.json();
      
      const jobId = data.job_id;
      setUploadStatus(data);
      setFile(null);
      
      // Status fetch and polling
      await fetchJobStatus(jobId);
      pollJobStatus(jobId);
    } catch (err) {
      setError(`Upload error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (jobId, fileType = null) => {
    try {
      const job = jobs[jobId];
      const tagType = job?.tag_type || 'tag';

      if (tagType === 'udt') {
        const downloadFile = async (type) => {
          const response = await robustFetch(joinUrl(config.apiBase, `download/${jobId}?file_type=${type}`), {
            headers: getHeaders()
          });
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const originalName = job?.filename || 'data';
          const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
          a.download = `converted_${nameWithoutExt}_${type}.json`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        };

        if (fileType) {
          await downloadFile(fileType);
        } else {
          await downloadFile('types');
          await downloadFile('instances');
        }
      } else {
        const response = await robustFetch(joinUrl(config.apiBase, `download/${jobId}`), {
          headers: getHeaders()
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const originalName = job?.filename || 'data';
        const extension = originalName.split('.').pop();
        a.download = `converted_${originalName.replace(`.${extension}`, '')}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      setError('Download error: ' + err.message);
    }
  };

  const deleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job and its files?')) {
      return;
    }

    try {
      await robustFetch(joinUrl(config.apiBase, `job/${jobId}`), {
        method: 'DELETE',
        headers: getHeaders(),
        mode: 'cors'
      });
      
      setJobs(prev => {
        const newJobs = { ...prev };
        delete newJobs[jobId];
        return newJobs;
      });
      if (pollingRef.current[jobId]) {
        clearInterval(pollingRef.current[jobId]);
        delete pollingRef.current[jobId];
      }
    } catch (err) {
      setError('Delete error: ' + err.message);
    }
  };

  const getTypeBadge = (type) => {
    const isUdt = type === 'udt';
    return (
      <span style={{ 
        padding: '0.1rem 0.5rem', 
        borderRadius: '12px', 
        fontSize: '0.65rem', 
        fontWeight: '700',
        background: isUdt ? '#e0e7ff' : '#f1f5f9',
        color: isUdt ? '#4338ca' : '#475569',
        textTransform: 'uppercase',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 'normal',
        border: `1px solid ${isUdt ? '#c7d2fe' : '#e2e8f0'}`,
        height: '1.2rem'
      }}>
        {type || 'tag'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      queued: { bg: '#fef3c7', color: '#92400e' },
      processing: { bg: '#dbeafe', color: '#1e40af' },
      completed: { bg: '#d1fae5', color: '#065f46' },
      failed: { bg: '#fee2e2', color: '#991b1b' }
    };
    const style = styles[status] || styles.queued;
    return (
      <span style={{ 
        padding: '0.35rem 0.75rem', 
        borderRadius: '20px', 
        fontSize: '0.75rem', 
        fontWeight: '600',
        background: style.bg,
        color: style.color
      }}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <div className="back-button" onClick={() => navigate('/')}>
        ← Back to Home
      </div>

      <div className="page-header">
        <h2 className="page-title">Tag Conversion Agent</h2>
        <p className="page-subtitle">
          Upload Excel/CSV files for async tag and UDT conversion processing
        </p>
      </div>

      <div className="card">
        <h3 className="card-title">Upload File</h3>
        <form onSubmit={handleUpload}>
          <div className="form-group">
            <label>File Type</label>
            <select value={fileType} onChange={(e) => setFileType(e.target.value)}>
              <option value="tag">Tag</option>
              <option value="udt">UDT</option>
            </select>
          </div>
          
          <div className="form-group">
            <label>Select File</label>
            <div className="file-upload" onClick={() => document.getElementById('file-input').click()}>
              <input
                id="file-input"
                type="file"
                accept=".xlsx,.csv"
                onChange={handleFileChange}
              />
              <div className="file-upload-label">
                {file ? (
                  <span style={{ color: '#10b981', fontWeight: 600 }}>{file.name}</span>
                ) : (
                  <>
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📄</div>
                    <div>Drop your file here or click to browse</div>
                    <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', color: '#94a3b8' }}>Supported: .xlsx, .csv</div>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button type="submit" className="btn btn-primary" disabled={loading || !file}>
              {loading ? 'Uploading...' : 'Upload File'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={fetchAllJobs} disabled={loading}>
              Refresh Jobs
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <div className="alert-content">
            <div className="alert-title">Error</div>
            <div>{error}</div>
          </div>
        </div>
      )}

      {uploadStatus && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          <div className="alert-content">
            <div className="alert-title">Upload Successful</div>
            <div>Job ID: {uploadStatus.job_id}</div>
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="card-title">Conversion Jobs</h3>
        {Object.keys(jobs).length === 0 ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>
            No jobs found. Upload a file to get started.
          </p>
        ) : (
          <div className="job-list">
            {Object.entries(jobs)
              .sort(([, a], [, b]) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
              .map(([jobId, job]) => (
                <div key={jobId} className="job-item">
                  <div className="job-info">
                    <div className="job-filename" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      {job.filename || 'Unknown'}
                      {getTypeBadge(job.tag_type)}
                    </div>
                    <div className="job-meta">
                      ID: {jobId} • {formatDateSafe(job.created_at)}
                      {job.file_size && ` • ${(parseInt(job.file_size) / 1024).toFixed(1)} KB`}
                    </div>
                    {job.error && (
                      <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                        Error: {job.error}
                      </div>
                    )}
                  </div>
                  <div className="job-actions">
                    {getStatusBadge(job.status)}
                    {job.status === 'completed' && (
                      job.tag_type === 'udt' ? (
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleDownload(jobId, 'types')}
                          >
                            Types
                          </button>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleDownload(jobId, 'instances')}
                          >
                            Instances
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleDownload(jobId)}
                        >
                          Download
                        </button>
                      )
                    )}
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => deleteJob(jobId)}
                      style={{ padding: '0.5rem', background: '#fee2e2', color: '#ef4444' }}
                      title="Delete Job"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default UploadJobs;
